import { useState, useRef, useEffect } from 'react'
import { Play, Pause, Volume2, VolumeX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { getLatestEpisode } from '@/services/rssService'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface AudioPlayerProps {
  className?: string
}

export function AudioPlayer({ className }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(100)
  const [progress, setProgress] = useState(0)
  const [playbackRate, setPlaybackRate] = useState("1.0")
  const [episodeData, setEpisodeData] = useState({ title: '', audioUrl: '' })

  useEffect(() => {
    const fetchLatestEpisode = async () => {
      try {
        const latest = await getLatestEpisode()
        setEpisodeData(latest)
      } catch (error) {
        console.error('Erro ao carregar episódio:', error)
      }
    }

    fetchLatestEpisode()
  }, [])

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
      } else {
        audioRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted
      setIsMuted(!isMuted)
    }
  }

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0]
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100
      setVolume(newVolume)
    }
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100
      setProgress(progress)
    }
  }

  const handleProgressChange = (value: number[]) => {
    const newProgress = value[0]
    if (audioRef.current) {
      const time = (audioRef.current.duration / 100) * newProgress
      audioRef.current.currentTime = time
      setProgress(newProgress)
    }
  }

  const handlePlaybackRateChange = (value: string) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = parseFloat(value)
      setPlaybackRate(value)
    }
  }

  return (
    <div className={`flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b dark:border-gray-700 ${className}`}>
      <div className="flex items-center gap-4 flex-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={togglePlay}
          className="text-[#FF3366] hover:text-[#FF3366]/80"
        >
          {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
        </Button>
        
        <div className="flex items-center gap-2 min-w-[120px]">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMute}
            className="text-[#FF3366] hover:text-[#FF3366]/80"
          >
            {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </Button>
          <Slider
            value={[isMuted ? 0 : volume]}
            max={100}
            step={1}
            className="w-20"
            onValueChange={handleVolumeChange}
          />
        </div>

        <Select value={playbackRate} onValueChange={handlePlaybackRateChange}>
          <SelectTrigger className="w-[80px]">
            <SelectValue placeholder="1.0x" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0.5">0.5x</SelectItem>
            <SelectItem value="0.75">0.75x</SelectItem>
            <SelectItem value="1.0">1.0x</SelectItem>
            <SelectItem value="1.25">1.25x</SelectItem>
            <SelectItem value="1.5">1.5x</SelectItem>
            <SelectItem value="2.0">2.0x</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex-1 mx-4">
          <Slider
            value={[progress]}
            max={100}
            step={0.1}
            className="w-full"
            onValueChange={handleProgressChange}
          />
        </div>
        
        <span className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
          {episodeData.title || 'Carregando...'}
        </span>
      </div>

      <audio
        ref={audioRef}
        src={episodeData.audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onVolumeChange={(e) => setVolume(e.currentTarget.volume * 100)}
      />
    </div>
  )
} 